import React from 'react';
import { Button, Container, Paper, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { defineMessages, useIntl } from 'react-intl';
import { useMutation, useQueryClient } from 'react-query';
import API, { useAuth, useCsrfToken } from '@wheeebo/polaris-api';
import { useErrorHandler } from '../errorHandler';
import { ValidatableField } from '../form';
import { ConfirmationDialog } from '../dialog';
import Head from './Head';
const msgs = defineMessages({
    errorNoUser: {
        id: 'pages.member.EmailUpdatePage.error.noUesr',
        defaultMessage: 'user not found',
    },
    errorValueNotFound: {
        id: 'pages.member.EmailUpdatePage.error.valueNotFound',
        defaultMessage: 'value not found',
    },
    errorInvalidEmail: {
        id: 'pages.member.EmailUpdatePage.error.invalidEmail',
        defaultMessage: 'invalid email',
    },
    headerTitle: {
        id: 'pages.member.EmailUpdatePage.header.title',
        defaultMessage: 'Change Email Address',
    },
    textContent: {
        id: 'pages.member.EmailUpdatePage.text.content',
        defaultMessage: 'Please enter your current email and password, and the new email address. We are going to ' +
            'send you a verification email to the new address.',
    },
    inputCurrentEmailTitle: {
        id: 'pages.member.EmailUpdatePage.input.currentEmailTitle',
        defaultMessage: 'Current Email',
    },
    inputNewEmailTitle: {
        id: 'pages.member.EmailUpdatePage.input.newEmailTitle',
        defaultMessage: 'New Email',
    },
    inputNewEmailPlaceholder: {
        id: 'pages.member.EmailUpdatePage.input.newEmailPlaceholder',
        defaultMessage: 'Enter your new email',
    },
    buttonCancel: {
        id: 'pages.member.EmailUpdatePage.button.cancel',
        defaultMessage: 'Cancel',
    },
    buttonChangeEmail: {
        id: 'pages.member.EmailUpdatePage.button.changeEmail',
        defaultMessage: 'Change Email',
    },
    dialogConfirmationTitle: {
        id: 'pages.member.EmailUpdatePage.dialog.confirmationTitle',
        defaultMessage: 'Confirm',
    },
    dialogConfirmationContent: {
        id: 'pages.member.EmailUpdatePage.dialog.confirmationContent',
        defaultMessage: 'Are you really going to change your email? After the change request is submitted, ' +
            'your account is invalidated and redirected to the login page. Please sign in with ' +
            'your new email address.',
    },
});
function EmailUpdatePage() {
    const intl = useIntl();
    const { csrfToken } = useCsrfToken();
    const { account } = useAuth();
    const navigate = useNavigate();
    const { register, formState, handleSubmit: validate } = useForm();
    const { errors } = formState;
    const { showAndEmitJsErrorEvent, showError } = useErrorHandler();
    const [email, setEmail] = React.useState();
    const [newEmail, setNewEmail] = React.useState();
    const [confirmationDialogOpen, setConfirmationDialogOpen] = React.useState(false);
    const queryClient = useQueryClient();
    const { mutate: updateAccount, isLoading } = useMutation((props) => API.Accounts.update(csrfToken, props), {
        onSuccess: () => {
            queryClient.invalidateQueries(API.Accounts.CacheKey);
            setTimeout(() => navigate('/'), 1000);
        },
        onError: showAndEmitJsErrorEvent,
    });
    const handleGoBack = React.useCallback(() => navigate('/settings'), [navigate]);
    const handleSubmit = React.useCallback(() => {
        if (!account) {
            showError(intl.formatMessage(msgs.errorNoUser));
            return;
        }
        else if (!email || !newEmail) {
            showError(intl.formatMessage(msgs.errorValueNotFound));
            return;
        }
        else if (email !== newEmail) {
            showError(intl.formatMessage(msgs.errorInvalidEmail));
            return;
        }
        setConfirmationDialogOpen(true);
    }, [email, newEmail, showError, intl, account]);
    const handleConfirmationCancel = React.useCallback(() => setConfirmationDialogOpen(false), []);
    const handleConfirmationOk = React.useCallback(async () => {
        setConfirmationDialogOpen(false);
        if (!account) {
            showError(intl.formatMessage(msgs.errorNoUser));
            return;
        }
        else if (!email || !newEmail) {
            showError(intl.formatMessage(msgs.errorValueNotFound));
            return;
        }
        else if (email !== newEmail) {
            showError(intl.formatMessage(msgs.errorInvalidEmail));
            return;
        }
        updateAccount({ email, newEmail });
    }, [email, newEmail, showError, updateAccount, intl, account]);
    return (React.createElement(Container, { sx: { display: 'flex', justifyContent: 'center' } },
        React.createElement(Head, { title: intl.formatMessage(msgs.headerTitle) }),
        React.createElement("form", { onSubmit: validate(handleSubmit) },
            React.createElement(Paper, { sx: { width: 400, mt: 8, p: 3 } },
                React.createElement(Stack, { gap: 3 },
                    React.createElement(Typography, { align: "center", variant: "h6" }, intl.formatMessage(msgs.headerTitle)),
                    intl.formatMessage(msgs.textContent),
                    React.createElement(ValidatableField, { id: "email", type: "email", label: intl.formatMessage(msgs.inputCurrentEmailTitle), fullWidth: true, disabled: isLoading, errors, register, registerOptions: {
                            required: true,
                            pattern: /\S+@\S+\.\S+/,
                            maxLength: 128,
                            onChange: (e) => setEmail(e.target.value),
                        } }),
                    React.createElement(ValidatableField, { id: "newEmail", type: "email", label: intl.formatMessage(msgs.inputNewEmailTitle), placeholder: intl.formatMessage(msgs.inputNewEmailPlaceholder), fullWidth: true, disabled: isLoading, errors, register, registerOptions: {
                            required: true,
                            pattern: /\S+@\S+\.\S+/,
                            maxLength: 128,
                            onChange: (e) => setNewEmail(e.target.value),
                        } }),
                    React.createElement(Stack, { direction: "row", gap: 1 },
                        React.createElement(Button, { disabled: isLoading, fullWidth: true, onClick: handleGoBack, size: "medium" }, intl.formatMessage(msgs.buttonCancel)),
                        React.createElement(Button, { fullWidth: true, disabled: isLoading || !email || !newEmail, type: "submit", size: "medium" }, intl.formatMessage(msgs.buttonChangeEmail)))))),
        React.createElement(ConfirmationDialog, { open: confirmationDialogOpen, title: intl.formatMessage(msgs.dialogConfirmationTitle), description: intl.formatMessage(msgs.dialogConfirmationContent), onCancel: handleConfirmationCancel, onConfirm: handleConfirmationOk })));
}
export default React.memo(EmailUpdatePage);
